import React, { useState, useEffect } from 'react';
import { Linkedin, Instagram, ChevronDown } from 'lucide-react';
import zeugmaBg from '../img/zeugma.jpg';

const GaziantepLanding = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getNavbarStyle = () => {
    return 'absolute top-0 left-0 right-0 z-50 py-6';
  };

  return (
    <div className="relative w-full overflow-x-hidden">
      {/* Navbar - Standard Layout, No Menu Links */}
      <nav className={getNavbarStyle()}>
        <div className="max-w-[1920px] mx-auto px-8 lg:px-16">
          <div className="flex items-center justify-center">
            {/* Center Logo - GWS Icon Only */}
            <div
              className="flex flex-col items-center group cursor-default"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_wealth-legacy-2/artifacts/vulhojge_gws.png"
                alt="GWS Logo"
                className="h-12 w-auto group-hover:scale-110 transition-transform duration-500"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen w-full overflow-hidden">
        {/* Background Image & Overlay (Full Screen) */}
        <div className="absolute inset-0">
          <img
            src={zeugmaBg}
            alt="Zeugma Gaziantep"
            className="w-full h-full object-cover"
          />
          {/* Multi-layer Overlay matching Home */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a441f]/70 via-[#1a441f]/50 to-[#1a441f]/90" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a441f]/30 to-transparent" />
        </div>

        {/* Content Container */}
        <div className="relative z-10 flex flex-col justify-center items-center h-full w-full px-8 text-center">
          <div className="max-w-4xl">
            {/* Mobile Logo */}
            <div className="lg:hidden mb-6 flex justify-center">
              <img 
                src="https://www.renatravel.com/wp-content/uploads/2025/05/rena-travel-white-logo.svg" 
                alt="Rena Travel" 
                className="w-32 opacity-90 hidden" 
              />
            </div>

            <h1 className="text-[#f8f8f8] text-5xl lg:text-8xl font-bold leading-tight mb-6 drop-shadow-lg" style={{ fontFamily: "'Spirits Soft', 'Figtree', sans-serif" }}>
              Zeugma'dan Sofraya Gaziantep
            </h1>
            
            <div className="text-[#f8f8f8] text-2xl lg:text-3xl font-bold mb-8 drop-shadow-md">
              3 - 5 Nisan 2026
            </div>
          </div>
        </div>

        {/* Bottom Description */}
        <div className="absolute bottom-24 left-0 right-0 z-10 px-8 text-center flex justify-center">
            <p className="text-[#e4a483] text-base lg:text-xl font-medium leading-relaxed max-w-2xl drop-shadow-md">
              Zeynep Topçuoğlu’nun ev sahipliğinde sofranın bereketinden tarihin derin izlerine uzanan Gaziantep’i deneyimliyoruz.
            </p>
        </div>

        {/* Scroll Down Indicator */}
        <div 
          onClick={() => document.getElementById('program')?.scrollIntoView({ behavior: 'smooth' })}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 cursor-pointer animate-bounce group"
        >
          <div className="flex flex-col items-center gap-2">
            <ChevronDown className="w-8 h-8 text-white/60 group-hover:text-white/90 transition-colors" />
          </div>
        </div>
      </section>

      {/* Program Section */}
      <section id="program" className="bg-[#f9fafb] py-10 lg:py-16 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none grayscale" 
             style={{ 
               backgroundImage: `url("https://customer-assets.emergentagent.com/job_wealth-legacy-2/artifacts/vulhojge_gws.png")`,
               backgroundSize: '120px',
               backgroundRepeat: 'space'
             }} 
        />
        
        <div className="max-w-[1920px] mx-auto px-8 lg:px-16 relative z-10">
          <div className="flex flex-col gap-16 lg:gap-24">
            {/* Day 1 */}
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 items-center">
              {/* Image Side */}
              <div className="w-full lg:w-1/2">
                <div className="relative aspect-[4/3] overflow-hidden rounded-lg shadow-2xl">
                  <img
                    src="https://www.gaziantepkebaphan.com/upload/resim-365254.jpg"
                    alt="Gaziantep Karşılama"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>
              
              {/* Content Side */}
              <div className="w-full lg:w-1/2">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-full bg-[#1a441f] flex items-center justify-center text-[#e4a483] font-bold text-xl shrink-0">1</div>
                  <h3 className="text-[#1a441f] text-2xl lg:text-3xl font-bold" style={{ fontFamily: "'Spirits Soft', 'Figtree', sans-serif" }}>
                    Varış & Gaziantep Sofrasına Giriş
                  </h3>
                </div>
                
                <div className="mb-8">
                  <span className="text-[#587c8d] font-medium tracking-widest text-sm uppercase">3 Nisan 2026 — Cuma</span>
                </div>

                <ul className="space-y-4 text-[#587c8d] font-medium leading-relaxed list-none p-0 m-0">
                  <li className="block">
                    <span className="font-bold text-lg text-[#1a441f]">Uçuş Bilgileri:</span> Türk Hava Yolları TK2224 - 10.00 uçağı ile İstanbul Havalimanından hareket ediyor olacağız. Sabiha Gökçen Havalimanını tercih eden misafirler Pegasus Havayollarının 9.10 uçağını alabilirler. Rena Travel ekibi bizleri Gaziantep Havalimanında karşılıyor olacak.
                  </li>
                  <li className="block">
                    <span className="font-bold text-lg text-[#1a441f]">12.30:</span> Küşleme Kebaphan'da Öğle yemeği
                  </li>
                  <li className="block">
                    <span className="font-bold text-lg text-[#1a441f]">13.30:</span> Şehre Küstü Konakları'na transfer hafif yürüyüş ve Şahinbey Milli Mücadele Müzesi ziyareti
                  </li>
                  <li className="block">
                    <span className="font-bold text-lg text-[#1a441f]">16.00:</span> Divan Gaziantep Oteline giriş ve dinlenme
                  </li>
                  <li className="block">
                    <span className="font-bold text-lg text-[#1a441f]">19.00:</span> Anadolu Han Gane'de fasıl eşliğinde akşam yemeği ve eğlence
                  </li>
                </ul>
              </div>
            </div>

            {/* Day 2 */}
            <div className="flex flex-col lg:flex-row-reverse gap-12 lg:gap-24 items-center">
              {/* Image Side */}
              <div className="w-full lg:w-1/2">
                <div className="relative aspect-[4/3] overflow-hidden rounded-lg shadow-2xl">
                  <img
                    src="https://pbs.twimg.com/media/EWCmQVUXkAAYUYB.jpg"
                    alt="Gaziantep Mutfağı"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>
              
              {/* Content Side */}
              <div className="w-full lg:w-1/2">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-full bg-[#1a441f] flex items-center justify-center text-[#e4a483] font-bold text-xl shrink-0">2</div>
                  <h3 className="text-[#1a441f] text-2xl lg:text-3xl font-bold" style={{ fontFamily: "'Spirits Soft', 'Figtree', sans-serif" }}>
                    Mozaikler, Çarşılar & Geleneksel Mutfağın İzinde
                  </h3>
                </div>
                
                <div className="mb-8">
                  <span className="text-[#587c8d] font-medium tracking-widest text-sm uppercase">4 Nisan 2026 — Cumartesi</span>
                </div>

                <ul className="space-y-4 text-[#587c8d] font-medium leading-relaxed list-none p-0 m-0">
                  <li className="block">
                    <span className="font-bold text-lg text-[#1a441f]">09.30:</span> Otel lobisinde rehber ile buluşma
                  </li>
                  <li className="block">
                    <span className="font-bold text-lg text-[#1a441f]">10.00:</span> Orkide Pastanesi – İbrahimli Şubesi’nde kahvaltı
                  </li>
                  <li className="block">
                    <span className="font-bold text-lg text-[#1a441f]">11.30:</span> Zeugma Mozaik Müzesi ziyareti
                  </li>
                  <li className="block">
                    <span className="font-bold text-lg text-[#1a441f]">13.30:</span> Omaç Gaziantep Mutfağı’nda öğle yemeği
                  </li>
                  <li className="block">
                    <span className="font-bold text-lg text-[#1a441f]">15.00:</span> Bakırcılar Çarşısı ve tarihi hanlar gezisi – alışveriş ve keşif için serbest zaman
                  </li>
                  <li className="block">
                    <span className="font-bold text-lg text-[#1a441f]">16.30:</span> Tarihi Tahmis Kahvesi’nde menengiç ve zahter kahvesi molası
                  </li>
                  <li className="block">
                    <span className="font-bold text-lg text-[#1a441f]">17.30:</span> Divan Gaziantep Oteline dönüş ve dinlenme
                  </li>
                  <li className="block">
                    <span className="font-bold text-lg text-[#1a441f]">19.00:</span> Otel Lobisinde rehber ile buluşma
                  </li>
                  <li className="block">
                    <span className="font-bold text-lg text-[#1a441f]">19.30:</span> Tarihi Bey Mahallesi ziyareti
                  </li>
                  <li className="block">
                    <span className="font-bold text-lg text-[#1a441f]">20.00:</span> Topçuoğlu Antep Evi’nde özel bir akşam yemeği
                  </li>
                </ul>
              </div>
            </div>

            {/* Day 3 */}
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 items-center">
              {/* Image Side */}
              <div className="w-full lg:w-1/2">
                <div className="relative aspect-[4/3] overflow-hidden rounded-lg shadow-2xl">
                  <img
                    src="https://www.bizevdeyokuz.com/wp-content/uploads/gaziantepte-baklava-nerede-yenir.jpg"
                    alt="Gaziantep Tatlıları"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>
              
              {/* Content Side */}
              <div className="w-full lg:w-1/2">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-full bg-[#1a441f] flex items-center justify-center text-[#e4a483] font-bold text-xl shrink-0">3</div>
                  <h3 className="text-[#1a441f] text-2xl lg:text-3xl font-bold" style={{ fontFamily: "'Spirits Soft', 'Figtree', sans-serif" }}>
                    Antep'te Son Tatlar & Dönüş
                  </h3>
                </div>
                
                <div className="mb-8">
                  <span className="text-[#587c8d] font-medium tracking-widest text-sm uppercase">5 Nisan 2026 — Pazar</span>
                </div>

                <ul className="space-y-4 text-[#587c8d] font-medium leading-relaxed list-none p-0 m-0">
                  <li className="block">
                    <span className="font-bold text-lg text-[#1a441f]">09.30:</span> Ünal lokantasında Pazar kahvaltısı
                  </li>
                  <li className="block">
                    <span className="font-bold text-lg text-[#1a441f]">11.30:</span> Güllüoğlu’nda bülbül yuvası tadımı
                  </li>
                  <li className="block">
                    <span className="font-bold text-lg text-[#1a441f]">12.00:</span> Elmacı Pazarı’nda serbest zaman
                  </li>
                  <li className="block">
                    <span className="font-bold text-lg text-[#1a441f]">14.00:</span> İmam Çağdaş’ta öğle yemeği
                  </li>
                  <li className="block">
                    <span className="font-bold text-lg text-[#1a441f]">16.00:</span> Özel araç ile Gaziantep Havalimanına transfer
                  </li>
                  <li className="block">
                    <span className="font-bold text-lg text-[#1a441f]">Uçuş bilgileri:</span> Türk Hava Yolları TK2229 - 18.35 uçağı ile Gaziantep Havalimanından hareket ediyor olacağız. Sabiha Gökçen Havalimanına dönüşü tercih eden misafirler Pegasus Havayollarının 20.05 uçağını alabilirler.
                  </li>
                </ul>
              </div>
            </div>

            {/* Important Information */}
            <div>
              <h3 className="text-[#1a441f] text-2xl lg:text-3xl font-bold mb-8" style={{ fontFamily: "'Spirits Soft', 'Figtree', sans-serif" }}>
                Önemli Bilgilendirmeler
              </h3>
              
              <div className="space-y-6 text-[#587c8d] leading-relaxed">
                <p>
                  Bu seyahat programı, GWS üyeleri için <a href="https://www.renatravel.com/" target="_blank" rel="noopener noreferrer" className="underline text-[#e4a483] hover:text-[#e4a483] transition-colors">Rena Travel</a> tarafından özel olarak hazırlanmıştır. Program içeriği ve deneyimlerle ilgili daha detaylı bilgi almak isteyenler WhatsApp üzerinden GWS’ye ulaşabilir.
                </p>
                <p>
                  Program, Türk Hava Yolları’nın İstanbul Havalimanı gidiş-dönüş belirtilen uçuş saatlerine göre organize edilmiştir. Farklı saatlerde seyahat etmeyi tercih eden misafirlerimizin, transfer ve bağlantılarını bireysel olarak organize etmeleri beklenmektedir.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GaziantepLanding;
